import React from "react"
import SectionCard from "../../../shared/components/SectionCard"
import EmptyStateBlock from "../../../shared/components/EmptyStateBlock"
import StatusPill from "../../../shared/components/StatusPill"

const MERGE_COUNT_LABELS = {
  appointments: "Citas",
  payments: "Pagos",
  treatmentLogs: "Tratamientos",
  treatmentPlans: "Planes",
  accountReceivables: "Cuentas por cobrar",
  odontogramEntries: "Odontograma",
  odontogramSurfaces: "Superficies",
  medicalHistories: "Historia médica",
  clinicalHistories: "Historias clínicas",
  clinicalForms: "Formularios",
  financialAuditEvents: "Eventos financieros",
  clinicalSpecialtyCases: "Casos clínicos",
  clinicalSpecialtyEvolutions: "Evoluciones clínicas"
}

function formatMergeCounts(counts = {}) {
  return Object.entries(counts || {})
    .filter(([, value]) => Number(value || 0) > 0)
    .map(([key, value]) => ({
      key,
      label: MERGE_COUNT_LABELS[key] || key,
      value: Number(value || 0)
    }))
}


function getTimelineVisual(item) {
  const type = String(item?.eventType || item?.type || "").trim().toUpperCase()

  switch (type) {
    case "APPOINTMENT":
      return { badge: "Cita", tone: "success", accent: "#16a34a" }
    case "TREATMENT":
      return { badge: "Tratamiento", tone: "info", accent: "#0284c7" }
    case "PLAN":
      return { badge: "Plan", tone: "info", accent: "#7c3aed" }
    case "ODONTOGRAM":
      return { badge: "Odontograma", tone: "neutral", accent: "#a21caf" }
    case "PAYMENT":
    case "PAYMENT_REGISTERED":
      return { badge: "Pago", tone: "success", accent: "#2563eb" }
    case "PAYMENT_CANCELLED":
      return { badge: "Pago cancelado", tone: "danger", accent: "#dc2626" }
    case "RECEIVABLE":
      return { badge: "CxC", tone: "warning", accent: "#d97706" }
    default:
      return { badge: "Evento", tone: "neutral", accent: "#64748b" }
  }
}

export default function PatientTimelineTab({
  loadingPatientTimeline,
  patientTimeline,
  patientMergeAudits,
  loadingPatientMergeAudits,
  formatDateTime,
  formatMoney
}) {
  const rows = Array.isArray(patientTimeline) ? patientTimeline : []
  const mergeAudits = Array.isArray(patientMergeAudits) ? patientMergeAudits : []
  const isLoading = loadingPatientTimeline || loadingPatientMergeAudits

  return (
    <SectionCard title="Actividad" compact>
      {isLoading ? (
        <EmptyStateBlock title="Cargando actividad" compact />
      ) : !rows.length && !mergeAudits.length ? (
        <EmptyStateBlock
          title="Sin movimientos registrados"
          text="Cuando existan citas, tratamientos, pagos o planes, aparecerán aquí."
          compact
        />
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <StatusPill tone="neutral">Eventos: {rows.length}</StatusPill>
          </div>

          {mergeAudits.length > 0 && (
            <div style={{ display: "grid", gap: 10 }}>
              {mergeAudits.map((audit) => {
                const movedRows = formatMergeCounts(audit.movedCounts)
                const remainingRows = formatMergeCounts(audit.remainingCounts)

                return (
                  <div
                    key={audit.id}
                    style={{
                      border: "1px solid #bfdbfe",
                      borderRadius: 18,
                      background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)",
                      padding: 14,
                      display: "grid",
                      gap: 10,
                      boxShadow: "0 14px 30px rgba(30,64,175,0.08)"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div style={{ display: "grid", gap: 4, minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <div style={{ fontWeight: 950, color: "#1e3a8a", fontSize: 14 }}>
                            Fusión de paciente duplicado
                          </div>
                          <StatusPill tone="info">Auditoría</StatusPill>
                        </div>

                        <div style={{ color: "#475569", fontSize: 13, marginTop: 2, lineHeight: 1.4 }}>
                          Consolidación segura: {audit.sourcePatientName || "Paciente origen"} → {audit.targetPatientName || "Paciente destino"}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                        gap: 10
                      }}
                    >
                      <div style={{ border: "1px solid #dbeafe", background: "#ffffff", borderRadius: 14, padding: "10px 12px", display: "grid", gap: 4 }}>
                        <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>Origen</div>
                        <div style={{ color: "#0f172a", fontWeight: 900 }}>{audit.sourcePatientName || "-"}</div>
                      </div>

                      <div style={{ border: "1px solid #dbeafe", background: "#ffffff", borderRadius: 14, padding: "10px 12px", display: "grid", gap: 4 }}>
                        <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>Destino</div>
                        <div style={{ color: "#0f172a", fontWeight: 900 }}>{audit.targetPatientName || "-"}</div>
                      </div>

                      <div style={{ border: "1px solid #dbeafe", background: "#ffffff", borderRadius: 14, padding: "10px 12px", display: "grid", gap: 4 }}>
                        <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>Ejecutó</div>
                        <div style={{ color: "#0f172a", fontWeight: 900 }}>{audit.mergedByUserName || "Usuario del sistema"}</div>
                      </div>

                      <div style={{ border: "1px solid #dbeafe", background: "#ffffff", borderRadius: 14, padding: "10px 12px", display: "grid", gap: 4 }}>
                        <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>Fecha</div>
                        <div style={{ color: "#0f172a", fontWeight: 900 }}>{formatDateTime(audit.createdAt)}</div>
                      </div>
                    </div>

                    {movedRows.length > 0 ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {movedRows.map((row) => (
                          <span
                            key={row.key}
                            style={{
                              border: "1px solid #c7d2fe",
                              background: "#eef2ff",
                              color: "#3730a3",
                              borderRadius: 999,
                              padding: "5px 9px",
                              fontSize: 12,
                              fontWeight: 850
                            }}
                          >
                            {row.label}: {row.value}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#64748b", fontSize: 13 }}>
                        No se registraron movimientos transferidos.
                      </div>
                    )}

                    {remainingRows.length > 0 ? (
                      <div style={{ color: "#92400e", fontSize: 12, fontWeight: 800 }}>
                        Atención: quedaron movimientos pendientes en origen.
                      </div>
                    ) : null}

                    {audit.notes ? (
                      <div style={{ color: "#64748b", fontSize: 12 }}>
                        {audit.notes}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}

          {rows.map((item) => {
            const visual = getTimelineVisual(item)

            return (
              <div
                key={item.id}
                style={{
                  borderRadius: 16,
                  border: "1px solid #e2e8f0",
                  borderLeft: `4px solid ${visual.accent}`,
                  background: "#ffffff",
                  padding: 13,
                  display: "grid",
                  gap: 8
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "flex-start"
                  }}
                >
                  <div style={{ display: "grid", gap: 4, minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        flexWrap: "wrap"
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 900,
                          fontSize: 15,
                          color: "#0f172a",
                          lineHeight: 1.2,
                          wordBreak: "break-word"
                        }}
                      >
                        {item.title || "Movimiento"}
                      </div>

                      <StatusPill tone={visual.tone}>{visual.badge}</StatusPill>
                    </div>

                    {item.subtitle ? (
                      <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.4 }}>
                        {item.subtitle}
                      </div>
                    ) : null}
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                      fontWeight: 800,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {formatDateTime(item.occurredAt)}
                  </div>
                </div>

                {item.description ? (
                  <div style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.45 }}>
                    {item.description}
                  </div>
                ) : null}

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {item.amount != null ? (
                    <StatusPill tone="neutral">Monto: {formatMoney(item.amount || 0)}</StatusPill>
                  ) : null}

                  {item.balance != null ? (
                    <StatusPill tone="info">Saldo: {formatMoney(item.balance || 0)}</StatusPill>
                  ) : null}

                  {item.status ? (
                    <StatusPill tone={statusTone(item.status)}>
                      {translateGenericStatus(item.status)}
                    </StatusPill>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </SectionCard>
  )
}

function statusTone(status) {
  const raw = String(status || "").trim().toUpperCase()
  if (["CONFIRMED", "POSTED", "PAID", "DONE", "COMPLETED"].includes(raw)) return "success"
  if (["PENDING", "PARTIAL"].includes(raw)) return "warning"
  if (["CANCELLED", "FAILED"].includes(raw)) return "danger"
  if (["SCHEDULED", "BOOKED"].includes(raw)) return "info"
  return "neutral"
}

function translateGenericStatus(status) {
  const raw = String(status || "").trim().toUpperCase()
  if (raw === "CONFIRMED") return "Confirmada"
  if (raw === "POSTED") return "Registrado"
  if (raw === "PAID") return "Pagado"
  if (raw === "DONE" || raw === "COMPLETED") return "Realizado"
  if (raw === "PENDING") return "Pendiente"
  if (raw === "PARTIAL") return "Parcial"
  if (raw === "CANCELLED") return "Cancelado"
  if (raw === "FAILED") return "Fallido"
  if (raw === "SCHEDULED" || raw === "BOOKED") return "Programado"
  if (!raw) return "-"
  return raw
}
